import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, someTween } from '../skillUsage';

export const elementMorphHandoffSkill: CodeoSkill = {
    name: SKILL_NAMES.elementMorphHandoff,
    kind: 'codeo',
    description:
        'Hand off between two states by morphing the shape around them — a card, pill, badge or ' +
        'box that reshapes from the first state into the second at the same spot while its ' +
        'contents crossfade, so the eye tracks one continuous object. Needs that shape: two bare ' +
        'words or figures swapping is a crossfade, not this.',
    checkLooksFor:
        'a container whose shape changes (its borderRadius, background or boxShadow tweened) while the contents inside it crossfade',
    documentUsesSkill: check(
        (script) =>
            someTween(script, /\b(?:borderRadius|background|boxShadow):/) &&
            someTween(script, /(?:opacity|autoAlpha):\s*0\b/) &&
            someTween(script, /(?:opacity|autoAlpha):\s*1\b/),
    ),
    instructions: `# Element Morph Handoff

Instead of cutting (or sweeping) between two on-screen states, let one element *become* the next, within the same scene. The element is a **shape around the content** (a card, a pill, a badge, a box), and that shape is what morphs: a wide product card squeezes into a round-cornered button, a square tile rounds into a pill. The content inside crossfades while the shape changes, so the viewer's eye locks onto the shape as the anchor through the handoff. That is far more premium than a slideshow cut, and unlike \`${SKILL_NAMES.momentSweepTransition}\` it ties two specific elements together rather than wiping the whole canvas.

**It needs that shape.** Two bare words or figures, one scaling out as the other scales in, have nothing to morph: that is a crossfade, not this technique. For bare type, use \`${SKILL_NAMES.typeAsObject}\` (a word becoming the object it names), \`${SKILL_NAMES.facetMorphShape}\` (one drawn silhouette reshaping into another), or a match cut. If the design has no container, give both states one (a badge behind each figure, a pill behind each label), or pick another handoff.

Don't tween \`width\`/\`height\` for the size change — use uniform \`scale\` plus paint-only \`borderRadius\`/\`background\`/\`boxShadow\`. These morph the *apparent* shape without a layout tween; layout tweens reflow every frame and jitter under H.264 (same reasoning as \`${SKILL_NAMES.statBarsAndFills}\`).

## Markup

The card is one element holding both states' contents in one grid cell, so they share its centre. The new content starts at \`opacity: 0\` in CSS so it isn't visible before the morph. Each content block is its own text leaf with a unique \`id\`.

\`\`\`html
<div class="grid place-items-center" style="position: absolute; inset: 0;">
  <div id="morph-card" class="grid place-items-center"
    style="overflow: hidden; padding: 48px 96px; border-radius: 28px; background: var(--l5-color-0, #101820)">
    <div id="morph-card-old"
      style="grid-area: 1/1; font-family: var(--l5-font-0), sans-serif; font-size: 96px; font-weight: 800; color: var(--l5-color-3, #ffffff)">Acme</div>
    <div id="morph-card-new"
      style="grid-area: 1/1; opacity: 0; font-family: var(--l5-font-0), sans-serif; font-size: 64px; font-weight: 700; color: var(--l5-color-3, #ffffff)">Find out more</div>
  </div>
</div>
\`\`\`

## Timeline code

The card changes apparent shape (scale, corner radius, fill) while its contents crossfade:

\`\`\`js
const tl = gsap.timeline({ paused: true });
const MORPH_AT = 1.2, MORPH_DUR = 0.9;
// Shape change via scale + paint-only props (never width/height)
tl.to('#morph-card', { scale: 0.55, borderRadius: 999, background: 'var(--l5-color-1, #7a6dff)',
    duration: MORPH_DUR, ease: 'power2.inOut' }, MORPH_AT);
// Old content fades over the first ~40% of the morph
tl.to('#morph-card-old', { opacity: 0, duration: MORPH_DUR * 0.4, ease: 'power1.in' }, MORPH_AT);
// New content fades in over the last ~40%
tl.to('#morph-card-new', { opacity: 1, duration: MORPH_DUR * 0.4, ease: 'power1.out' },
    MORPH_AT + MORPH_DUR * 0.6);
window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Hold a beat first** (0.6–1.5s) so the viewer registers state 1 before it morphs — an instant morph reads as a glitch.
- **Ease**: \`power2.inOut\` (balanced) or \`power3.inOut\` (snappier) for the shape change. Avoid overshoot on the morph itself — it fights the dimensional change.
- **Crossfade timing**: old content fades early, new content fades late, leaving a brief "shape-only" moment between — that gap is the natural blink the handoff hides behind.
- **Final handoff** (optional): tween the morph card's opacity to 0 over the last 0.1–0.2s to reveal a real next element in the same scene behind it — but only if that element is *pixel-identical* to the card's final state, or you get a visible pop. Use DOM order for stacking (render the anchor before the card), never snap \`z-index\` mid-fade.

## Pitfalls

- **No shape, no morph.** Bare content scaling out while other bare content scales in is a crossfade. The shape around the content has to be there and has to change.
- **No \`width\`/\`height\` tweens** — uniform \`scale\` for size, \`borderRadius\`/\`background\`/\`boxShadow\` for the shape/surface read.
- **\`overflow: hidden\` on the morph card** so its contents clip as the corner radius changes.
- Use \`background\` (not \`backgroundColor\`) when morphing to/from a gradient — GSAP interpolates gradients only when both states have the same number of stops.
- Both contents share one grid cell (\`grid-area: 1/1\`) inside the card so they morph at the same spot — offset centers make the swap look like a cut.
- The morph card and its contents are GSAP-tweened on \`scale\`/\`opacity\` — keep no CSS \`transform\` baked onto them; an entrance's from-vars carry the resting start state instead.`,
};
